/* eslint-disable camelcase */
import React from 'react';
import {connect} from 'react-redux';
import {
  Layout,
  Link,
  FooterHelp,
  PageActions,
  Heading,
  Stack,
  TextContainer,
  TextStyle,
} from '@shopify/polaris';
import {StepsFooterHelp} from './helpers/footer_help';
import {notifyException} from 'utilities/analytics/bugsnag';
import {Routes} from 'sections/services-marketplace/experts/utilities';
import GetMatchRecommendations, {
  GetMatchRecommendationsQueryData,
} from './graphql/GetMatchRecommendations.graphql';
import ExpertInviteMutation, {
  ExpertInviteMutationData,
} from './graphql/InviteExpertMutation.graphql';
import {getServicesMarketplacePublicClient} from 'utilities/graphql';
import {
  SkeletonExpertCard,
  PageHeader,
  InviteExpertCard,
} from 'sections/services-marketplace/experts/components';

const client = getServicesMarketplacePublicClient();

interface OwnProps {
  brief: {
    id: string;
    subcategory_short_label: string;
    shop_permanent_domain: string;
    service_handle: string;
  };
  organization: {
    businessName: string;
  };
  browseContext: boolean;
}

interface State {
  profiles?: GetMatchRecommendationsQueryData.CurrentShopBriefProfileListingRecommendations[];
  loading: boolean;
  invitedHandles: string[];
}

class ConfirmationInvite extends React.Component<OwnProps, State> {
  state: State = {
    profiles: undefined,
    loading: false,
    invitedHandles: [],
  };

  componentDidMount() {
    this.fetchRecommendedExperts();
  }

  onInviteExpert = async (organizationHandle: string) => {
    const {invitedHandles} = this.state;
    const {
      brief: {id},
    } = this.props;
    const {data} = await client.mutate<ExpertInviteMutationData>({
      mutation: ExpertInviteMutation,
      variables: {briefId: id, organizationHandle},
    });

    if (
      data &&
      data.expertInvite &&
      data.expertInvite.userErrors.length === 0
    ) {
      this.setState({
        invitedHandles: [...invitedHandles, organizationHandle],
      });
    } else {
      notifyException({
        error: new Error('Failed to invite expert to job'),
        meta: {briefId: id, organizationHandle},
      });
    }
  };

  async fetchRecommendedExperts() {
    const {
      brief: {id},
    } = this.props;

    this.setState({loading: true});

    const {
      data: {
        currentShop: {
          brief: {profileListingRecommendations},
        },
      },
    } = await client.query<{
      currentShop: GetMatchRecommendationsQueryData.CurrentShop;
    }>({
      query: GetMatchRecommendations,
      variables: {briefId: id},
    });

    this.setState({profiles: profileListingRecommendations, loading: false});
  }

  renderHeader() {
    const {
      brief: {subcategory_short_label},
      organization,
    } = this.props;

    const pageHeaderDescription = `Your job for ${subcategory_short_label} has been sent to ${organization.businessName}. View your job in your Shopify admin to manage conversations with experts.`;

    return (
      <Layout.Section>
        <PageHeader
          title="Job Sent"
          description={pageHeaderDescription}
          image={Routes.imagePath('svg/confirmation.svg')}
        />
      </Layout.Section>
    );
  }

  renderRecommendedExperts() {
    const {profiles, loading, invitedHandles} = this.state;
    const {
      brief: {service_handle},
    } = this.props;

    return (
      <Layout.Section>
        <div className="ConfirmationRecommendedExperts">
          <Stack vertical>
            <TextContainer>
              <Heading>Request more quotes (optional)</Heading>
              <TextStyle variation="subdued">
                Compare quotes from other experts for this job.
              </TextStyle>
            </TextContainer>
            <div aria-live="polite">
              {loading
                ? [...Array(5).keys()].map(key => (
                    <SkeletonExpertCard key={key} />
                  ))
                : profiles &&
                  profiles.length &&
                  profiles.map(profile => {
                    if (!profile) return null;
                    return (
                      <InviteExpertCard
                        invited={invitedHandles.includes(profile.handle)}
                        profile={profile}
                        onInviteExpert={this.onInviteExpert}
                        key={profile.handle}
                        serviceHandle={service_handle}
                      />
                    );
                  })}
            </div>
          </Stack>
        </div>
      </Layout.Section>
    );
  }

  renderHelp() {
    const {submission: help} = StepsFooterHelp;

    return help ? (
      <Layout.Section>
        <FooterHelp>
          Learn more about{' '}
          <Link url={help.url} external>
            {help.about}
          </Link>
          .
        </FooterHelp>
      </Layout.Section>
    ) : null;
  }

  renderPageActions() {
    const {
      brief: {shop_permanent_domain, id},
    } = this.props;

    return (
      <Layout.Section>
        <PageActions
          primaryAction={{
            content: 'View Job',
            onAction: () => {
              window.location.assign(Routes.jobPath(shop_permanent_domain, id));
            },
          }}
        />
      </Layout.Section>
    );
  }

  render() {
    return (
      <>
        {this.renderHeader()}
        {this.renderRecommendedExperts()}
        {this.renderPageActions()}
        {this.renderHelp()}
      </>
    );
  }
}

function mapStateToProps(store: any) {
  const {
    progress: {brief},
    context: {organization, browseContext},
  } = store;

  return {
    organization,
    brief,
    browseContext,
  };
}

export default connect(mapStateToProps)(ConfirmationInvite);
